import { useState, useEffect, useMemo } from 'react';
import { apiClient } from 'axios-client';
import { getOrderedCallSources } from 'pages/dashboard/utils/call-source-display';

export interface MonthlyData {
  month: string;
  [key: string]: string | number;
}

export interface AggregateData {
  [key: string]: number;
  total: number;
}

interface UseCallSourcesDataParams {
  after?: Date;
  before?: Date;
  bilikZoneIds?: number[];
  proViewIds?: number[];
  additionalFilters?: any[];
  skipFetch?: boolean;
  unique?: boolean;
  mode?: 'aggregate' | 'monthly';
}

interface CallSourceResult {
  loading: boolean;
  error: Error | null;
  rawData: any[];
  monthlyData: MonthlyData[];
  aggregateData: AggregateData | undefined;
  availableSources: string[];
}

const getEndpoint = (mode: string, unique: boolean): string => {
  if (mode === 'aggregate') {
    return unique ? 'event/unique-calls-count' : 'event/calls-count';
  }
  return unique
    ? 'event/unique-bilik-calls-by-month'
    : 'event/bilik-calls-by-month';
};

const createAggregateData = (rawData: any[]): MonthlyData[] => {
  const data = { month: 'aggregate' };
  rawData.forEach((item) => (data[item.source] = item.count));
  return [data];
};

const createMonthlyData = (rawData: any[]): MonthlyData[] => {
  const months = {};
  rawData.forEach((item) => {
    const month = item.month || 'unknown';
    if (!months[month]) months[month] = { month };
    months[month][item.source] = item.count;
  });
  return Object.values(months);
};

const computeStats = (
  data: MonthlyData[],
  sources: string[],
): AggregateData => {
  const totals: Record<string, number> = sources.reduce(
    (acc, source) => {
      acc[source] = data.reduce(
        (sum, item) => sum + (Number(item[source]) || 0),
        0,
      );
      return acc;
    },
    {} as Record<string, number>,
  );

  const total = Object.values(totals).reduce(
    (sum: number, count: number) => sum + count,
    0,
  );

  return { ...totals, total };
};

const processCallSourceData = (rawData: any[], mode: string) => {
  if (!rawData.length) {
    return {
      data: [],
      availableSources: [],
      stats: undefined,
    };
  }

  const uniqueSources = Array.from(
    new Set(
      rawData
        .map((item) => item.source)
        .filter((source) => source != null && source !== ''),
    ),
  );
  const sources = getOrderedCallSources(uniqueSources);

  const data: MonthlyData[] =
    mode === 'aggregate'
      ? createAggregateData(rawData)
      : createMonthlyData(rawData);

  const stats = mode === 'aggregate' ? computeStats(data, sources) : undefined;

  return { data, availableSources: sources, stats };
};

export const useCallSourcesData = ({
  after,
  before,
  bilikZoneIds,
  proViewIds,
  additionalFilters = [],
  skipFetch = false,
  unique = false,
  mode = 'monthly',
}: UseCallSourcesDataParams): CallSourceResult => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [rawData, setRawData] = useState<any[]>([]);

  useEffect(() => {
    if (skipFetch) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const endpoint = getEndpoint(mode, unique);

        const result = await apiClient.post(endpoint, {
          after: after?.toISOString(),
          before: before?.toISOString(),
          bilikZoneIds,
          proViewIds,
          filters: additionalFilters,
          groupBySource: true,
        });

        setRawData(result.data || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    after,
    before,
    bilikZoneIds,
    proViewIds,
    skipFetch,
    unique,
    mode,
    JSON.stringify(additionalFilters),
  ]);

  const {
    data: monthlyData,
    availableSources,
    stats: aggregateData,
  } = useMemo(() => processCallSourceData(rawData, mode), [rawData, mode]);

  return {
    loading,
    error,
    rawData,
    monthlyData,
    aggregateData,
    availableSources,
  };
};
